


























































































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import {
  PdfTemplateCatalogueCategory,
  PdfTemplateFieldMapping,
} from "@/data/models/ClientAdministrationRow";
import { COLOR_PRIMARY, COLOR_PRIMARY_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import {
  cloudProblemStatus,
  COMMA_VALUE,
  fieldDescriptionParts,
  MAX_TRANSFORMATION_TEXT_LENGTH,
  mappingsKey,
  offeredValues,
  PdfTemplateEditorRow,
  PdfTemplateValueOption,
  previewText,
  takesText,
  transformationText,
  TransformationType,
  withCategory,
  withTransformationText,
  withValueAdded,
  withValueMoved,
  withValueMovedBefore,
  withValueRemoved,
} from "@/utils/PdfTemplateMapping";

/** What the page hands the dialog to edit one field. */
export interface PdfTemplateFieldMappingDialogArgs {
  row: PdfTemplateEditorRow;
  catalogue: PdfTemplateCatalogueCategory[];
  /** Labels and IRES Cloud statuses of every value, legacy ones included. */
  options: Map<string, PdfTemplateValueOption>;
  /** The stored category's label when the catalogue no longer offers that category. */
  storedCategoryLabel: string | null;
  /** Only the HyDry certificate is filled by IRES Cloud, so only there are its statuses shown. */
  showsCloudStatuses: boolean;
}

/**
 * Links one form field of a PDF template to a category and an ordered list of values, with text before
 * or after a value, as the IRES Admin editor does. Opened from the template page in edit mode only, so
 * it opens editing; ÜBERNEHMEN hands the field's new mapping back to the page, whose SICHERN saves it.
 *
 * `show()` resolves with the new mapping (`values` empty to unlink the field), or null when cancelled.
 */
@Component({ components: { "base-dialog": BaseDialogComponent, "confirmation-dialog": ConfirmationDialogComponent } })
export default class PdfTemplateFieldMappingDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;
  row: PdfTemplateEditorRow | null = null;
  draft: PdfTemplateFieldMapping = { fieldName: "", category: "", values: [], transformations: [] };
  catalogue: PdfTemplateCatalogueCategory[] = [];
  options: Map<string, PdfTemplateValueOption> = new Map();
  storedCategoryLabel: string | null = null;
  showsCloudStatuses = false;
  addKey = 0;
  /** The row whose handle is pressed: only that row may start a drag. */
  armedIndex: number | null = null;
  dragIndex: number | null = null;
  /** Where a drop would land: before this row, or `values.length` for the end. */
  dropBefore: number | null = null;

  private initialKey = "";
  private resolve: ((mapping: PdfTemplateFieldMapping | null) => void) | null = null;

  readonly comma = COMMA_VALUE;
  readonly maxTextLength = MAX_TRANSFORMATION_TEXT_LENGTH;

  show(args: PdfTemplateFieldMappingDialogArgs): Promise<PdfTemplateFieldMapping | null> {
    this.row = args.row;
    this.catalogue = args.catalogue;
    this.options = args.options;
    this.storedCategoryLabel = args.storedCategoryLabel;
    this.showsCloudStatuses = args.showsCloudStatuses;
    this.draft = args.row.mapping
      ? { ...args.row.mapping, values: [...args.row.mapping.values], transformations: [...args.row.mapping.transformations] }
      : { fieldName: args.row.fieldName, category: "", values: [], transformations: [] };
    this.initialKey = mappingsKey([this.draft]);
    this.addKey++;
    // Persistent and closeOnSave false: edit mode is never clicked away, and ÜBERNEHMEN closes itself.
    this.baseDialog.show({
      title: this.$t("admin.pdfTemplates.edit.dialogTitle", [args.row.fieldName]).toString(),
      positiveButtonText: this.$t("common.apply").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      neutralPositiveButtonText: this.$t("admin.pdfTemplates.edit.unlink").toString(),
      closeOnNeutralPositiveAction: false,
      maxWidth: 760,
      scrollable: true,
      persistent: true,
      closeOnSave: false,
    });
    this.onButtonsChanged();

    return new Promise((resolve) => (this.resolve = resolve));
  }

  get isCheckBox(): boolean {
    return this.row?.type === "CHECK_BOX";
  }

  get showsTexts(): boolean {
    return takesText(this.row?.type ?? null);
  }

  /** "Textfeld", "Ankreuzfeld · Gruppe aus 3 Feldern", or that the PDF no longer has it. */
  get fieldDescription(): string {
    return (this.row ? fieldDescriptionParts(this.row) : [])
      .map((part) => (part.count === undefined ? this.$t(part.key) : this.$tc(part.key, part.count)).toString())
      .join(" · ");
  }

  /** The catalogue's categories, plus a stored one it no longer offers, so the select can show it. */
  get categoryItems(): Array<{ name: string; label: string }> {
    const items = this.catalogue.map((category) => ({ name: category.name, label: category.label }));
    const stored = this.row?.mapping?.category ?? "";

    if (stored !== "" && !items.some((item) => item.name === stored)) {
      items.push({ name: stored, label: this.storedCategoryLabel || stored });
    }
    return items;
  }

  get valueItems(): PdfTemplateValueOption[] {
    return offeredValues(this.catalogue, this.draft.category, this.row?.type ?? null, this.options);
  }

  get preview(): string {
    return previewText(this.draft, (value) => this.labelOf(value));
  }

  get hasChanged(): boolean {
    return mappingsKey([this.draft]) !== this.initialKey;
  }

  labelOf(value: string): string {
    return this.options.get(value)?.label ?? value;
  }

  /** Why IRES Cloud leaves the value blank, on the certificate's template only; else null. */
  problemOf(value: string): string | null {
    const status = cloudProblemStatus(value, this.options, this.showsCloudStatuses);
    return status === null ? null : this.$t(`admin.pdfTemplates.statuses.${status}`).toString();
  }

  textOf(value: string, type: TransformationType): string {
    return transformationText(this.draft, value, type);
  }

  onCategoryChanged(category: string) {
    this.draft = withCategory(this.draft, category);
    this.addKey++;
  }

  onAdd(value: string | null) {
    if (value) {
      this.draft = withValueAdded(this.draft, value, this.row?.type ?? null);
    }
    this.addKey++;
  }

  onRemove(index: number) {
    this.draft = withValueRemoved(this.draft, index);
  }

  onMove(index: number, delta: number) {
    this.draft = withValueMoved(this.draft, index, delta);
  }

  /**
   * Where the dragged value will land: a line above the row it goes in front of, or below the last
   * row for the end. Inline, because the theme does not expose its colours as CSS variables.
   */
  dropIndicatorStyle(index: number): Record<string, string> {
    const last = this.draft.values.length - 1;
    const color = themed(COLOR_PRIMARY, COLOR_PRIMARY_DARK);

    if (this.dropBefore === index) {
      return { boxShadow: `inset 0 2px 0 0 ${color}` };
    }
    if (this.dropBefore === this.draft.values.length && index === last) {
      return { boxShadow: `inset 0 -2px 0 0 ${color}` };
    }
    return {};
  }

  onDragStart(index: number, event: DragEvent) {
    this.dragIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      // Firefox starts a drag only when some data is set.
      event.dataTransfer.setData("text/plain", String(index));
    }
  }

  onDragOver(index: number) {
    if (this.dragIndex !== null) {
      this.dropBefore = index;
    }
  }

  /** Over the card below the last row: the drop lands at the end. */
  onDragOverEnd() {
    if (this.dragIndex !== null) {
      this.dropBefore = this.draft.values.length;
    }
  }

  onDrop(before: number) {
    if (this.dragIndex !== null) {
      this.draft = withValueMovedBefore(this.draft, this.dragIndex, before);
    }
    this.onDragEnd();
  }

  onDragEnd() {
    this.armedIndex = null;
    this.dragIndex = null;
    this.dropBefore = null;
  }

  onTextChanged(value: string, type: TransformationType, text: string | null) {
    this.draft = withTransformationText(this.draft, value, type, text ?? "");
  }

  onUnlink() {
    this.draft = { ...this.draft, values: [], transformations: [] };
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after; LÖSEN only while something is linked. */
  @Watch("hasChanged")
  @Watch("draft.values.length")
  onButtonsChanged() {
    this.baseDialog.negativeButtonText = this.$t(this.hasChanged ? "common.abort" : "common.close").toString();
    this.baseDialog.showNeutralPositiveButton = this.draft.values.length > 0;
  }

  onApply() {
    this.close(this.draft);
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first (edit-mode-dialogs.md). */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  private close(result: PdfTemplateFieldMapping | null) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: PdfTemplateFieldMapping | null) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
